import * as Sentry from '@sentry/react';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import {
  type DehydratedState,
  HydrationBoundary,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { type ReactNode, useState } from 'react';

export const QueryClientCustomProvider = ({
  children,
  dehydratedState,
}: { children: ReactNode; dehydratedState: DehydratedState }) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retryDelay: (attemptIndex: number) =>
              Math.min(1000 * 2 ** attemptIndex, 30_000),
          },
        },
        queryCache: new QueryCache({
          onError: (error: any) => {
            console.log(error);
            Sentry.captureException(error);
          },
        }),
      } as any),
  );

  if (typeof window !== 'undefined') {
    const persister = createSyncStoragePersister({
      storage: window.localStorage,
    });
    persistQueryClient({
      queryClient,
      persister,
      maxAge: 1000 * 60 * 60 * 3, // 3h
    });
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        {children}
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition={'top-right'}
        />
      </HydrationBoundary>
    </QueryClientProvider>
  );
};
